import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { loginRequest } from "../../authConfig";
import {
  Badge,
  Box,
  Button,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Typography,
} from "@mui/material";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import DescriptionIcon from "@mui/icons-material/Description";
import GroupsIcon from "@mui/icons-material/Groups";
import { styled } from "@mui/system";
import NotificationsIcon from "@mui/icons-material/Notifications";
import {
  BadgeContainer,
  NavBar,
  NavigationBarButton,
  NavigationBarContainer,
  ToolsImg,
  ToolsImgWhite,
} from "./NavigationBar.styled";
import { Link } from "react-router-dom";
import { React, useState, useCallback, useEffect, useMemo } from "react";
import logo from "../../shared/icons/bloom_bright_logo_white_horizontal.svg";
import clientsSvg from "../../shared/icons/sidebarMenu/clients.svg";
import { AuthenticatedPage } from "../AuthenticatedPage";
import { SignatureDowloadTab } from "../signatureDownload/SignatureDowloadTab";
// b2cPolicies import removed — auth moved to Entra External ID.
import { useGetUserInfoQuery, useGetUsersRolesQuery, useGetMyDailyAlertsQuery, useGetProfilePictureQuery, useGetClientServiceHistoryQuery, useGetClientServiceByServiceIdQuery } from "../../services/ClientService";
import { useGetServicesDetailQuery } from "../../services/UtilityService";
import { useTabNavigation } from "../../hooks/useTabNavigation";
import { SessionInfoDialog } from "../calendar/SessionInfoDialog";
import { useSessionServiceActions } from "../calendar/useSessionServiceActions";
import { ServiceHistoryDialog } from "../common/ServiceHistoryDialog";
import { Avatar } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LogoutIcon from "@mui/icons-material/Logout";

// Stable (module-level) avatar for the alert session dialog — mirrors the client grid.
// Defining it inside the component would remount it (and re-fetch the picture) on every
// nav-bar render, causing flicker.
const AlertAvatar = ({ clientId: cid }) => {
  const { data: pictureUrl } = useGetProfilePictureQuery({ id: cid, resolution: 'thumbnail' }, { skip: !cid });
  return (
    <Avatar src={pictureUrl || undefined} sx={{ width: 28, height: 28 }}>
      <PersonIcon sx={{ fontSize: 18 }} />
    </Avatar>
  );
};

// Headless loader: fetches one ClientService's full record and reports it up. Lets us
// enrich each alert client without calling a hook in a loop.
const ClientServiceDetailLoader = ({ clientServiceId, onData }) => {
  const { data } = useGetClientServiceByServiceIdQuery(clientServiceId, { skip: !clientServiceId });
  useEffect(() => {
    if (data) onData(clientServiceId, data);
  }, [clientServiceId, data, onData]);
  return null;
};

/**
 * SessionInfoDialog for the nav-bar alert bell.
 *
 * The daily-alert payload is a lean summary (no attendance/billing/provider/form version),
 * so on its own the dialog showed fewer fields than the same dialog opened from the client
 * grid — which enriches its lean row with the full ClientService record. This wrapper does
 * the same enrichment (one fetch per client) so both entry points render identically.
 */
const AlertSessionInfoDialog = ({ session, ...props }) => {
  const [detailById, setDetailById] = useState({});
  const onDetail = useCallback((id, data) => {
    setDetailById((prev) => (prev[id] === data ? prev : { ...prev, [id]: data }));
  }, []);

  const clients = session?.clients ?? [];
  const enriched = session
    ? {
        ...session,
        clients: clients.map((c) => {
          const d = detailById[c.id];
          if (!d) return c;
          return {
            ...c,
            attendanceStatus: d.attendanceStatus ?? c.attendanceStatus ?? null,
            billingStatus: d.billingStatus ?? c.billingStatus ?? null,
            levelOfCare: c.levelOfCare ?? d.levelOfCare ?? null,
            psychosocialAssessmentFormId:
              c.psychosocialAssessmentFormId ?? d.psychosocialAssessmentFormId ?? null,
            psychosocialAssessmentFormVersion:
              d.psychosocialAssessmentFormVersion ?? c.psychosocialAssessmentFormVersion ?? null,
            user: d.owner
              ? { firstName: d.owner.firstName, lastName: d.owner.lastName }
              : c.user,
            start: d.startDate ?? c.start,
            end: d.endDate ?? c.end,
          };
        }),
      }
    : session;

  return (
    <>
      {clients.map((c) =>
        c.id ? (
          <ClientServiceDetailLoader key={c.id} clientServiceId={c.id} onData={onDetail} />
        ) : null,
      )}
      <SessionInfoDialog session={enriched} {...props} />
    </>
  );
};

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    height: "15px",
    minWidth: "15px",
    padding: "0",
  },
}));
const StyledLink = styled(Link)(({ theme }) => ({
  font: theme.typography.link.font,
  color: theme.palette.primary.contrastText,
  textDecoration: "none",
  alignItems: "center",
  display: "flex",
}));

export const ImgContainer = styled(Box)`
  width: 120px;
  display: flex;
  justify-content: center;
`;

export const NavigationBar = () => {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const [anchorEl, setAnchorEl] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const { openPage, openClient, openService } = useTabNavigation();

  // Fetch user info and roles when authenticated
  const { data: userInfo } = useGetUserInfoQuery(undefined, { skip: !activeAccount });
  const { data: roles } = useGetUsersRolesQuery(undefined, { skip: !activeAccount });

  // Today's services + notes for the logged-in user — drives the alert bell.
  const pad = (n) => String(n).padStart(2, '0');
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  // Poll frequently so a service scheduled AFTER the user logged in shows up in the alert
  // bell without a page reload. Also refetch when the tab regains focus / the network
  // reconnects (setupListeners is enabled in the store), and on mount, for immediate freshness.
  const { data: dailyAlerts } = useGetMyDailyAlertsQuery(todayStr, {
    skip: !activeAccount,
    pollingInterval: 60 * 1000, // refresh every 60s
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const alertServices = dailyAlerts?.services ?? [];
  const alertNotes = dailyAlerts?.notes ?? [];

  // Authoritative all-day flag comes from the service definition (Service.IsAllDay) — the daily
  // alert payload doesn't carry it. NOTE: the lean dropdown/Service endpoint returns only {id,name},
  // so use the services DETAIL query, which includes isAllDay. Map serviceId → isAllDay so all-day
  // services show "All day" (in the list AND the dialog) instead of a timezone-fragile midnight time.
  const { data: servicesDetail } = useGetServicesDetailQuery(undefined, { skip: !activeAccount });
  const serviceAllDayMap = useMemo(() => {
    const m = {};
    (servicesDetail ?? []).forEach((s) => { m[String(s.id ?? s.Id)] = !!(s.isAllDay ?? s.IsAllDay); });
    return m;
  }, [servicesDetail]);

  // Must be declared before alertSessions so providerName can reference it.
  const fallbackName = `${activeAccount?.idTokenClaims?.given_name || ''} ${activeAccount?.idTokenClaims?.family_name || ''}`.trim();
  const apiFullName = `${userInfo?.firstName || ''} ${userInfo?.lastName || ''}`.trim();
  const displayName = apiFullName || fallbackName;

  // Group the flat services into sessions (same service + start time = one session,
  // multi-client = group) so each alert is a session — matching the calendar.
  const alertSessions = (() => {
    const map = new Map();
    alertServices.forEach((s) => {
      const key = `${s.serviceId}|${s.startDate}`;
      if (!map.has(key)) {
        const d = new Date(s.startDate);
        map.set(key, {
          key,
          title: s.serviceName || 'Service',
          serviceId: s.serviceId,
          start: s.startDate,
          end: s.endDate,
          // Prefer the service definition's flag; fall back to a midnight-start heuristic.
          allDay: serviceAllDayMap[String(s.serviceId)] ?? (d.getHours() === 0 && d.getMinutes() === 0),
          providerName: displayName,
          isEditable: false,
          clients: [],
        });
      }
      map.get(key).clients.push({
        id: s.id,
        clientId: s.clientId,
        clientFirstName: s.clientFirstName,
        clientLastName: s.clientLastName,
        clientName: s.clientName,
        levelOfCare: s.levelOfCare,
        psychosocialAssessmentFormId: s.psychosocialAssessmentFormId,
      });
    });
    return [...map.values()];
  })();
  const alertCount = alertSessions.length + alertNotes.length;

  const [bellAnchor, setBellAnchor] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [statusHistoryServiceId, setStatusHistoryServiceId] = useState(null);
  const {
    data: alertStatusHistory = [],
    isFetching: isAlertHistoryLoading,
    isError: isAlertHistoryError,
  } = useGetClientServiceHistoryQuery(statusHistoryServiceId, { skip: !statusHistoryServiceId });

  // Same attendance / mark-performed actions the calendar offers, so the alert dialog matches.
  const {
    attendanceOptions: alertAttendanceOptions,
    onSetAttendance: alertOnSetAttendance,
    onMarkPerformed: alertOnMarkPerformed,
    markPerformedBusyId: alertMarkPerformedBusyId,
    performWarningDialog: alertPerformWarning,
  } = useSessionServiceActions({
    patchClient: (id, patch) => setSelectedSession(prev => prev ? {
      ...prev,
      clients: prev.clients.map(c => c.id === id ? { ...c, ...patch } : c),
    } : prev),
  });

  const bellOpen = Boolean(bellAnchor);
  const fmtTime = (d) => {
    const dt = new Date(d);
    return dt.getHours() === 0 && dt.getMinutes() === 0 ? 'All day' : dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const roleName = roles?.find?.(r => String(r?.id) === String(userInfo?.roleId))?.name;

  // Multi-role aware: prefer the assigned-role list (UserRoleAssignment) returned by /Users/Me;
  // fall back to the legacy single RoleId. Yields the full set of role names for the account card.
  const assignedRoleList = Array.isArray(userInfo?.assignedRoles) ? userInfo.assignedRoles : [];
  const roleNames = assignedRoleList.length > 0
    ? assignedRoleList
        .map((a) => a?.roleName ?? a?.RoleName
          ?? roles?.find?.((r) => String(r?.id) === String(a?.roleId ?? a?.RoleId))?.name)
        .filter(Boolean)
    : (roleName ? [roleName] : []);
  const userInitials = (displayName || '')
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
  const userEmail = userInfo?.email ?? userInfo?.Email ?? activeAccount?.username ?? '';

  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleLoginRedirect = () => {
    instance.loginRedirect(loginRequest).catch((error) => console.log(error));
  };

  const handleLogoutRedirect = () => {
    // App-only sign-out: clear the local MSAL session + cached API token so the app returns
    // to its login screen. We intentionally do NOT navigate to the identity provider's
    // end-session endpoint (onRedirectNavigate returns false) — that would also sign the user
    // out of their underlying Microsoft / Google account. Signing back in is then a quick SSO.
    localStorage.removeItem('token');
    instance.logoutRedirect({
      account: instance.getActiveAccount(),
      onRedirectNavigate: () => false,
    });
  };

  const handleProfileEditRedirect = () => {
    // Profile editing will move to an Entra External ID user flow — no-op for now.
  };

  return (
    <>
      <NavBar>
        <AuthenticatedTemplate>
          <AuthenticatedPage>
            <NavigationBarContainer>
            <ImgContainer>
                <img src={logo} alt="Bloom Bright LLC" width="100" />
              </ImgContainer>

              <BadgeContainer onClick={(e) => setBellAnchor(e.currentTarget)}>
                <StyledBadge badgeContent={alertCount} overlap="circular" color="error" invisible={alertCount === 0}>
                  <NotificationsIcon sx={{ color: "#fff" }} fontSize="small" />
                </StyledBadge>
              </BadgeContainer>
              <Popover
                open={bellOpen}
                anchorEl={bellAnchor}
                onClose={() => setBellAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                slotProps={{ paper: { sx: { width: 340, maxHeight: 460 } } }}
              >
                <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid #eee' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>Today's schedule</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {now.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}
                  </Typography>
                </Box>
                {alertCount === 0 ? (
                  <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Nothing scheduled for today.</Typography>
                  </Box>
                ) : (
                  <List dense sx={{ py: 0 }}>
                    {alertSessions.length > 0 && (
                      <Typography sx={{ px: 2, pt: 1, fontSize: '11px', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                        Sessions ({alertSessions.length})
                      </Typography>
                    )}
                    {alertSessions.map((sess) => {
                      const isGroup = sess.clients.length > 1;
                      const oneName = sess.clients[0]?.clientName || `${sess.clients[0]?.clientFirstName ?? ''} ${sess.clients[0]?.clientLastName ?? ''}`.trim();
                      return (
                        <ListItemButton key={sess.key} onClick={() => { setBellAnchor(null); setSelectedSession(sess); }}>
                          {isGroup
                            ? <GroupsIcon fontSize="small" sx={{ color: '#2E8B57', mr: 1.25 }} />
                            : <EventAvailableIcon fontSize="small" sx={{ color: '#4281AB', mr: 1.25 }} />}
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography sx={{ fontSize: '13px', fontWeight: 500 }} noWrap>{sess.title}</Typography>
                                <Chip size="small" label={isGroup ? `Group · ${sess.clients.length}` : 'Individual'} color={isGroup ? 'success' : 'info'} sx={{ fontSize: '10px', height: 18 }} />
                              </Box>
                            }
                            secondary={`${sess.allDay ? 'All day' : fmtTime(sess.start)} · ${isGroup ? `${sess.clients.length} clients` : (oneName || 'Client')}`}
                            secondaryTypographyProps={{ sx: { fontSize: '11px' } }}
                          />
                        </ListItemButton>
                      );
                    })}
                    {alertNotes.length > 0 && <Divider sx={{ my: 0.5 }} />}
                    {alertNotes.length > 0 && (
                      <Typography sx={{ px: 2, pt: 1, fontSize: '11px', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>
                        Notes ({alertNotes.length})
                      </Typography>
                    )}
                    {alertNotes.map((n) => (
                      <ListItemButton key={`note-${n.id}`} onClick={() => { setBellAnchor(null); openPage('/calendar', 'Calendar'); }}>
                        <DescriptionIcon fontSize="small" sx={{ color: '#E65100', mr: 1.25 }} />
                        <ListItemText
                          primary={<Typography sx={{ fontSize: '13px', fontWeight: 500 }} noWrap>{n.text || n.quickNoteTypeName || 'Note'}</Typography>}
                          secondary={`${n.quickNoteTypeName ? `${n.quickNoteTypeName}` : 'Note'}${n.clientName ? ` · ${n.clientName}` : ''}`}
                          secondaryTypographyProps={{ sx: { fontSize: '11px' } }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                )}
                <Box sx={{ p: 1, borderTop: '1px solid #eee', textAlign: 'center' }}>
                  <Button size="small" onClick={() => { setBellAnchor(null); openPage('/calendar', 'Calendar'); }}>
                    Open calendar
                  </Button>
                </Box>
              </Popover>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  marginRight: "33px",
                }}
              >
              </Box>
              <NavigationBarButton
                onClick={handleClick}
                sx={{
                  gap: 1,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: '10px',
                  border: '1px solid rgba(255,255,255,0.28)',
                  backgroundColor: open ? 'rgba(255,255,255,0.22)' : 'rgba(255,255,255,0.10)',
                  transition: 'background-color 0.15s ease',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.22)' },
                }}
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', fontSize: 13, fontWeight: 700 }}>
                  {userInitials}
                </Avatar>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', minWidth: 0, maxWidth: 180 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.25 }} noWrap>
                    {displayName}
                  </Typography>
                  {roleNames.length > 0 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: '100%' }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.9)', fontSize: 11, lineHeight: 1.3 }} noWrap>
                        {roleNames[0]}
                      </Typography>
                      {roleNames.length > 1 && (
                        <Chip
                          label={`+${roleNames.length - 1}`}
                          size="small"
                          sx={{ height: 16, fontSize: 10, color: '#fff', bgcolor: 'rgba(255,255,255,0.28)', '& .MuiChip-label': { px: 0.75 } }}
                        />
                      )}
                    </Box>
                  )}
                </Box>
                <KeyboardArrowDownIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.9)' }} />
              </NavigationBarButton>
              <Menu
                id="menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                MenuListProps={{ "aria-labelledby": "basic-button", sx: { pt: 0 } }}
                slotProps={{ paper: { sx: { mt: 1, minWidth: 268, borderRadius: 2 } } }}
              >
                {/* Account card header — avatar, name, email, and every assigned role */}
                <Box sx={{ px: 2, pt: 1.75, pb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 44, height: 44, bgcolor: 'primary.main', color: '#fff', fontSize: 16, fontWeight: 700 }}>
                      {userInitials}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary', lineHeight: 1.25 }} noWrap>
                        {displayName}
                      </Typography>
                      {userEmail && (
                        <Typography sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.3 }} noWrap>
                          {userEmail}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {roleNames.length > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.75 }}>
                        {roleNames.length > 1 ? `Roles (${roleNames.length})` : 'Role'}
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {roleNames.map((rn, i) => (
                          <Chip
                            key={`${rn}-${i}`}
                            label={rn}
                            size="small"
                            sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: 'rgba(101,157,189,0.16)', color: '#2f5d76' }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
                <Divider />
                <MenuItem onClick={() => { handleClose(); setProfileDialogOpen(true); }} sx={{ mt: 0.5 }}>
                  <PersonIcon fontSize="small" sx={{ mr: 1.25, color: 'text.secondary' }} />
                  Profile
                </MenuItem>
                <MenuItem onClick={handleLogoutRedirect}>
                  <LogoutIcon fontSize="small" sx={{ mr: 1.25, color: 'text.secondary' }} />
                  Sign out
                </MenuItem>
              </Menu>
              <SignatureDowloadTab open={profileDialogOpen} onClose={() => setProfileDialogOpen(false)} />
            </NavigationBarContainer>
          </AuthenticatedPage>

        </AuthenticatedTemplate>
        <UnauthenticatedTemplate>
          <NavigationBarContainer>
            <StyledLink to="/">

            </StyledLink>
            <NavigationBarButton
              onClick={handleLoginRedirect}
              variant="outlined"
            >
              Sign in
            </NavigationBarButton>
          </NavigationBarContainer>
        </UnauthenticatedTemplate>
      </NavBar>
      <AlertSessionInfoDialog
        open={!!selectedSession}
        session={selectedSession}
        AvatarComponent={AlertAvatar}
        onClose={() => setSelectedSession(null)}
        onOpenClient={(clientId, name) => { setSelectedSession(null); openClient(clientId, name); }}
        onOpenClientForm={(c) => {
          const clientName = `${c.clientFirstName ?? ''} ${c.clientLastName ?? ''}`.trim();
          const tabTitle = clientName ? `${clientName} - ${selectedSession.title}` : selectedSession.title;
          openService(c.psychosocialAssessmentFormId, tabTitle, c.clientId, c.id, c.psychosocialAssessmentFormVersion ?? null);
          setSelectedSession(null);
        }}
        onEditService={() => { setSelectedSession(null); openPage('/calendar', 'Calendar'); }}
        onViewHistory={(c) => setStatusHistoryServiceId(c.id)}
        attendanceOptions={alertAttendanceOptions}
        onSetAttendance={alertOnSetAttendance}
        onMarkPerformed={alertOnMarkPerformed}
        markPerformedBusyId={alertMarkPerformedBusyId}
      />
      {alertPerformWarning}
      <ServiceHistoryDialog
        open={!!statusHistoryServiceId}
        onClose={() => setStatusHistoryServiceId(null)}
        title="Service Status History"
        rows={alertStatusHistory}
        isLoading={isAlertHistoryLoading}
        isError={isAlertHistoryError}
        emptyText="No status history for this service."
      />
    </>
  );
};
